import React, { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ListChildComponentProps, VariableSizeList } from 'react-window';

import Menu from '@mui/material/Menu';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import IconButton from '@mui/material/IconButton';
import { findOrFormatEnum } from 'utils/stringUtil';
import { GlobalState } from 'reducers/stateTypes';
import { setCnFilters, setCnPagination } from 'reducers/contactNoteListReducer';
import { TextField } from '@mui/material';
import { ReactComponent as FilterIcon } from 'assets/icons/filter-icon.svg';
import { ReactComponent as FilterIconActive } from 'assets/icons/filter-icon-active.svg';
import { JBColors, JBTheme } from '@juliusbaer-gcrm/gcrm-frontend-components';
import { HeadCellContext } from 'components/Common/DeprecatedTableHead';
import { DropdownOption } from 'components/Common/ValidatedDropdown';
import { NOOP } from 'utils/utils';
import { findStatusObject } from 'utils/contactNoteUtil';

import { TruncatedTextWithOptionalTooltip } from './TruncatedTextWithOptionalTooltip';

const styles = {
  passive: {
    color: JBColors.support.greenSmoke[80],
    fill: JBTheme.palette.primary.light,
  },
  activeFilter: {
    fill: JBTheme.palette.primary.main,
  },
  filterContainer: {
    position: 'relative',
    display: 'inline-flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
  },
  textInput: {
    marginTop: JBTheme.spacing(1),
    marginLeft: JBTheme.spacing(3.5),
    marginRight: JBTheme.spacing(3.5),
    '& .MuiFilledInput-inputMarginDense': {
      paddingLeft: JBTheme.spacing(1.5),
      paddingTop: JBTheme.spacing(1),
      paddingBottom: JBTheme.spacing(1),
      width: '100%',
    },
  },
};

const MENU_HEIGHT = 300;
const ITEM_HEIGHT = 50;
/**
 * Filter component that pops up when the icon is clicked.
 * @param values The displayed values (the values to be filtered by)
 * @param name The name of the filter.
 * @param context The context of the dropdown
 * @param disableFormat option to disable text format or not
 * @param customValues an array of dropdown options (value+label) to display instead of the automatically generated values
 */
const ColumnFilter = ({
  values,
  name,
  context,
  disableFormat = false,
  customValues,
  filter = {},
  setFilters = NOOP,
}: {
  values: string[];
  name: string;
  context: HeadCellContext;
  disableFormat?: boolean;
  filter?: object;
  setFilters: any;
  customValues?: DropdownOption[];
}) => {
  const dispatch = useDispatch();
  const noteFilters: any = useSelector((state: GlobalState) => state.contactNoteList.filters);

  const contactTypeOptions = useSelector((state: GlobalState) => state.resources.contactTypeOptions);
  const contactTypeLabel = useCallback(
    (contactType: string) => {
      return contactTypeOptions.find((item) => item.value === contactType)?.label || '';
    },
    [contactTypeOptions],
  );

  if (
    context === HeadCellContext.CONTACT_NOTES &&
    name === 'partnerFullName' &&
    noteFilters.partnerFullName &&
    !values.includes(noteFilters.partnerFullName[0])
  ) {
    values.push(noteFilters.partnerFullName[0]);
  }
  const onChange = (key: string[]) => {
    const newFilters = { ...filters, [name]: key };
    if (newFilters[name].length === 0) {
      delete newFilters[name];
    }
    if (context === HeadCellContext.CONTACT_NOTES) {
      dispatch(setCnPagination(0));
      dispatch(setCnFilters(newFilters));
    } else {
      setFilters(newFilters);
    }
  };

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [inputValue, setInputValue] = React.useState('');
  const cnFilters = useSelector((state: GlobalState) => state.contactNoteList.filters);
  const getFilters = () => {
    if (context === HeadCellContext.CONTACT_NOTES) {
      return cnFilters;
    } else {
      return filter;
    }
  };
  const filters: any = getFilters();
  const selected: any = filters[name] || [];

  const filteredValues = React.useMemo(
    () =>
      values.filter((v) =>
        String(v).toLocaleLowerCase().replaceAll('.', '').includes(inputValue.toLocaleLowerCase().replaceAll('.', '')),
      ),
    [values, inputValue],
  );

  function handleClick(event: React.MouseEvent<HTMLElement, MouseEvent>) {
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
    setInputValue('');
  }

  const handleCheck = (item: string) => () => {
    if (selected.includes(item)) {
      onChange(selected.filter((s: any) => s !== item));
    } else {
      onChange([...selected, item]);
    }
  };

  function handleInputChange(e: { target: { value: React.SetStateAction<string> } }) {
    setInputValue(e.target.value);
  }

  const listRef = useRef<VariableSizeList>(null);

  const sortedValues = useMemo(() => filteredValues.sort((a, b) => a?.localeCompare(b)), [filteredValues]);

  const getLabel = (val: string) => {
    if (customValues) {
      return customValues.find((c: any) => c.value === val)?.label || val;
    }
    if (disableFormat) return val;

    // Adapt these helpers to your context:
    if (name === 'contactType') return contactTypeLabel(val);
    if (name === 'status') return findStatusObject(val)?.label || '';
    if (name === 'createdByFullName' && val === null) return 'N/A';
    return findOrFormatEnum(val);
  };

  const renderRow = ({ index, style }: ListChildComponentProps) => {
    const val = sortedValues[index];
    return (
      <MenuItem key={val} value={val} disableRipple onClick={handleCheck(val)} style={style}>
        <Checkbox
          color="primary"
          id={`${name}-checkbox-${index}`}
          data-testid={`${name}-checkbox-${index}`}
          checked={selected.includes(val)}
        />
        <TruncatedTextWithOptionalTooltip text={getLabel(val)} id={`${name}-option-${index}`} />
      </MenuItem>
    );
  };

  return (
    <div>
      <IconButton
        aria-haspopup="true"
        id={`${name}-icon`}
        disableRipple
        disableFocusRipple
        onClick={handleClick}
        {...(selected.length > 0 && { sx: styles.activeFilter })}
        {...(selected.length === 0 && { sx: styles.passive })}
        size="large"
      >
        {selected.length > 0 ? <FilterIconActive /> : <FilterIcon />}
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        id={`${name}-menu`}
        data-testid={`${name}-menu`}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          style: {
            maxHeight: MENU_HEIGHT + 50,
            width: 400,
            overflow: 'hidden',
          },
        }}
      >
        <FormControl fullWidth>
          <TextField
            placeholder="Search a value..."
            value={inputValue}
            onChange={handleInputChange}
            InputProps={{ disableUnderline: true }}
            sx={styles.textInput}
            margin="dense"
            variant="filled"
          />
        </FormControl>
        <FormControl fullWidth>
          <VariableSizeList
            height={MENU_HEIGHT}
            width={400}
            itemSize={() => ITEM_HEIGHT}
            itemCount={sortedValues.length}
            overscanCount={10}
            ref={listRef}
          >
            {renderRow}
          </VariableSizeList>
        </FormControl>
      </Menu>
    </div>
  );
};

export default ColumnFilter;
